/**
 * FAQ page content (Issue #2212, extracted from FaqPage.tsx)
 *
 * Single source for web FaqPage and the native FAQ screen.
 */

import type { ContentDoc } from '../../types/content';

export const FAQ_DOC: ContentDoc = {
  slug: 'faq',
  title: ['FAQ'],
  subtitle: 'Frequently Asked Questions… and a few we made up',
  sectionDividers: true,
  groups: [
    [
      {
        title: ['What ', { t: 'em', v: 'is' }, ' ', { t: 'brand' }, ' exactly?'],
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move is a simple, fast, not-trying-to-be-Social-Media tool for running and cycling clubs. We help you create events, manage members, keep chats sane, and basically avoid duct-taping together five apps just to go on a group run. Think: ',
              { t: 'strong', v: 'community with less chaos.' },
            ],
          },
        ],
      },
      {
        // #3521 (owner decisions 2026-10-05): the CORE is free for every club,
        // forever. Zero purchase-adjacent copy: this doc renders inside the iOS
        // app, and docs/canonical/business/ios-subscription-copy.md allows none
        // until billing exists. The tier story lives on web-only /pricing (#567).
        title: ['Is ORO move free?'],
        blocks: [
          { type: 'p', inlines: ['The core is free for every club, forever.'] },
          {
            type: 'p',
            inlines: [
              'Events, RSVPs, check-ins, chat, and your members: the stuff a club runs on costs nothing.',
            ],
          },
        ],
      },
      {
        // #3521 (owner decision 2026-10-05): public events are viewable with no
        // account (App.tsx `event/:id` is unprotected); RSVP needs an account
        // AND club membership (`POST /events/:eventId/rsvp` runs requireAuth +
        // requireEventClubMembership); joining a club needs an account
        // (`POST /clubs/:slug/join` runs requireAuth). ADR-0019 §5: never ask a
        // club to move, switch, or leave anything; the entry is one event.
        // "in a browser" is interim: signed-out iOS users are sent to login
        // (#3532). Drop it when #3532 ships.
        title: ['Will my members have to switch?'],
        blocks: [
          { type: 'p', inlines: ['Nope. Start with one event.'] },
          {
            type: 'p',
            inlines: [
              'Share the link wherever your club already talks. Anyone can open a public event in a browser and see the plan without an account.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'To RSVP, they sign up and join your club. No password, ever.',
            ],
          },
          { type: 'p', inlines: ["If the first one is good, they'll be back for the next."] },
        ],
      },
      {
        title: ['Do you sell my data?'],
        blocks: [
          { type: 'p', inlines: ['Hard no.'] },
          {
            type: 'p',
            inlines: [
              'We\'re here to help your club move, not to auction off your shoe size and preferred electrolyte flavor to "trusted partners."',
            ],
          },
          { type: 'p', inlines: ['Your data stays yours. Always.'] },
        ],
      },
      {
        title: ['Do I need a password?'],
        blocks: [
          { type: 'p', inlines: ['Nope.'] },
          {
            type: 'p',
            inlines: [
              'So "password123", "letmein", and your dog\'s birthday are all safe. We never asked.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'Sign in with a magic link by email, or a code by text. Pick your poison.',
            ],
          },
          {
            type: 'p',
            inlines: ['One less thing rattling around in your password manager.'],
          },
        ],
      },
      {
        title: ['Why do you need my location sometimes?'],
        blocks: [
          {
            type: 'p',
            inlines: [
              "If you check in at an event, we make sure you're ",
              { t: 'em', v: 'actually there' },
              ' and not heroically checking in from your couch. You can also choose to share it in order to find clubs nearby.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'We don\'t store your coordinates. The moment we verify "yep, you\'re here," the location disappears into the void.',
            ],
          },
        ],
      },
      {
        title: ['What devices does ORO move work on?'],
        blocks: [
          { type: 'p', inlines: ["If it has a browser, you're good."] },
          { type: 'p', inlines: ["Phone, tablet, laptop, smart fridge… we don't judge."] },
          {
            // #3448 (corrected 2026-09-11: the iPhone app shipped to the App
            // Store 2026-09-10 — the old "in final testing / coming soon" line
            // was stale, Rule 57). The dedicated "Is there an app?" entry below
            // carries the detail.
            type: 'p',
            inlines: [
              'There is also an iPhone app on the App Store. Android is not available yet.',
            ],
          },
        ],
      },
      {
        title: ['Is there an app?'],
        blocks: [
          {
            type: 'p',
            inlines: [
              'Yes! There is an iPhone app on the App Store. It is available worldwide.',
            ],
          },
          {
            type: 'p',
            inlines: [
              // #3448: /ios (the _redirects 302), never the Apple URL inline;
              // webRender: 'anchor' so it is a real full-navigation <a> that
              // reaches the server redirect, not a client-side react-router
              // Link into the SPA catch-all.
              { t: 'link', v: 'Get the iPhone app', href: '/ios', webRender: 'anchor' },
              '.',
            ],
          },
          {
            type: 'p',
            inlines: [
              "No iPhone? No problem. The website works on any phone, tablet, or laptop with a browser.",
            ],
          },
          {
            type: 'p',
            inlines: [
              'Android is not here yet. When there is real news, you will hear it. No vague promises.',
            ],
          },
        ],
      },
      {
        title: ['Can I use ORO move for a cycling club? (Or a pickleball squad? A unicycle gang?)'],
        blocks: [
          { type: 'p', inlines: ['Absolutely.'] },
          { type: 'p', inlines: ['Two wheels, one wheel, no wheels. We support it.'] },
          {
            type: 'p',
            inlines: [
              'If you move through space and enjoy doing it with others, ORO move is your app.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'Want a feature added to support it? ',
              { t: 'link', v: 'Get in touch', href: '/contact' },
              '.',
            ],
          },
        ],
      },
      {
        title: ['Does ORO move track my runs or rides?'],
        blocks: [
          { type: 'p', inlines: ['Nope.'] },
          {
            type: 'p',
            inlines: [
              "We're not trying to replace your GPS watch or those ego-boosting mileage charts.",
            ],
          },
          {
            type: 'p',
            inlines: [
              'We focus on ',
              { t: 'strong', v: 'community' },
              ', not performance metrics.',
            ],
          },
        ],
      },
      {
        title: ['Why does the UI feel so minimal? Where is everything?'],
        blocks: [
          {
            type: 'p',
            inlines: ["Because you don't need a digital yard sale to find your next event."],
          },
          {
            type: 'p',
            inlines: [
              'ORO move keeps things intentionally simple so you can get to the good stuff without wading through seventeen buttons and a regret-filled Feed. Minimal isn\'t "missing." It\'s focus. It\'s calm. It\'s oxygen.',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'And the Feed?' },
              { t: 'break' },
              'No thanks. Doom-scrolling is death.',
              { t: 'break' },
              'Group movement is life.',
            ],
          },
          { type: 'p', inlines: ['See you outside!'] },
        ],
      },
      {
        title: ['Can I create multiple clubs?'],
        blocks: [
          { type: 'p', inlines: ['Yes.'] },
          { type: 'p', inlines: ["We won't stop you."] },
          { type: 'p', inlines: ['Become the commissioner of your local Run Empire.'] },
          {
            type: 'p',
            inlines: ['Just remember: every new club increases your group responsibilities.'],
          },
        ],
      },
      {
        title: ["Help! My club members won't RSVP."],
        blocks: [
          { type: 'p', inlines: ["We can't fix that."] },
          {
            type: 'p',
            inlines: [
              'We ',
              { t: 'em', v: 'do' },
              ' give them obvious RSVP buttons, though.',
            ],
          },
          { type: 'p', inlines: ['Peer pressure usually works better than technology.'] },
        ],
      },
      {
        title: ['Is there a dark mode?'],
        blocks: [
          { type: 'p', inlines: ['There is only dark mode. We call it Nightfall.'] },
          {
            type: 'p',
            inlines: ['The app launches with Nightfall.'],
          },
          { type: 'p', inlines: ['Light mode comes later, once we stop arguing about it.'] },
        ],
      },
      {
        title: ['What if I break something?'],
        blocks: [
          { type: 'p', inlines: ['Message us.'] },
          { type: 'p', inlines: ["We're nice, and we probably broke it first anyway."] },
        ],
      },
      {
        title: ['Who built ORO move?'],
        showDivider: false,
        blocks: [
          {
            type: 'p',
            inlines: [
              'A tiny crew of Appalachian yucks who love long runs, mountain air, and questionable amounts of plantains on their steak fajitas.',
            ],
          },
          {
            type: 'p',
            inlines: ['We make simple tools that help people get moving, together and on purpose.'],
          },
          { type: 'p', inlines: ["We're proud of this thing."] },
          { type: 'p', inlines: ['We hope you will be too.'] },
        ],
      },
    ],
  ],
};
